import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, Optional } from '@angular/core';
import { Namespace } from 'enums-lib';
import { ENVIRONMENT } from 'environments-lib';
import { IDataSourceApiService, IEntity, IEntityMetadata, IEnvironment, IMetaFunctionArgs, INamespace } from 'interfaces-lib';
import { jwtDecode } from 'jwt-decode';
import { BehaviorSubject, forkJoin, Observable, of } from 'rxjs';
import { catchError, filter, map, shareReplay, switchMap, take } from 'rxjs/operators';
import { Identity, IdentityStorage } from 'storage-service-lib';

export const SalesforceNamespace: INamespace = { name: 'salesforce', aliases: [] };

export enum SalesforceListTypes {
  FileAttachments = 'fileAttachments',
}

export enum SalesforceFileTypes {
  FileAttachment = 'fileAttachment',
}

interface IBody {
  [key: string]: any;
}

interface IResponse {
  totalSize?: number;
  done: boolean;
  records: any[];
}

interface IEntitySearchMap {
  [index: string]: IEntitySearchDetails;
}

interface IEntitySearchDetails {
  fields: string[];
  includeIsDeleted: boolean;
  queryFields?: string[];
  fieldMetadata?: Record<string, ISalesforceFieldMapping>;
}

// Salesforce mapping configuration interfaces
export interface ISalesforceFieldMapping {
  key: string;
  label?: string;
  path: string;
  dataType?: string;
}

export interface ISalesforceObjectMapping {
  id: string;
  label?: string;
  objectName: string;
  enabled: boolean;
  fields: ISalesforceFieldMapping[];
  display?: ISalesforceDisplayTemplates;
  relatedObjects?: ISalesforceRelatedObject[];
}

export interface ISalesforceRelatedObject {
  objectName: string;
  relationSegment: string;
}

export interface ISalesforceConfig {
  objects: ISalesforceObjectMapping[];
}

interface IAdv2Config {
  config?: {
    salesforce?: ISalesforceConfig;
  };
}

interface ISalesforceObjectSummary {
  name?: string;
  apiName?: string;
  label?: string;
  iconUrl?: string;
  iconColor?: string;
}

interface ISalesforceObjectInfo {
  label: string;
  iconUrl?: string;
  iconColor?: string;
}

export interface IEntityTypeInfo {
  objectName: string;
  label?: string;
  iconUrl?: string;
  iconColor?: string;
  queryFields: string[];
  fields: string[];
  fieldMetadata: ISalesforceFieldMapping[];
  display?: ISalesforceDisplayTemplates;
}

export interface ISalesforceDisplayTemplates {
  primary: string;
  secondary?: string;
}

@Injectable({ providedIn: 'root' })
export class SalesforceDataSourceApiService implements IDataSourceApiService {
  private entitySearchMap: IEntitySearchMap = {};
  private mappings$?: Observable<ISalesforceObjectMapping[]>;
  private objectInfos$?: Observable<Record<string, ISalesforceObjectInfo>>;
  private mappingLookup: Record<string, ISalesforceObjectMapping> = {};
  private objectInfoLookup: Record<string, ISalesforceObjectInfo> = {};
  private mappingSubject = new BehaviorSubject<ISalesforceObjectMapping[]>([]);
  private initialized$ = new BehaviorSubject<boolean>(false);
  private configLoadStarted = false;

  constructor(
    private http: HttpClient,
    private identityStorage: IdentityStorage,
    @Optional() @Inject(ENVIRONMENT) private environment?: Partial<IEnvironment>,
  ) {
    console.log('[SalesforceDataSource] ctor');
    this.initWhenIdentityReady();
  }

  /**
   * Get available entity types with their query fields for entity picking
   */
  getAvailableEntityTypes(): Observable<IEntityTypeInfo[]> {
    return this.mappingSubject.asObservable().pipe(
      map(() => {
        return Object.keys(this.entitySearchMap).map(objectName => {
          const entityInfo = this.entitySearchMap[objectName];
          const metadata = entityInfo?.fieldMetadata ?? {};
          const objectInfo = this.resolveObjectInfo(objectName);
          return {
            objectName,
            label: objectInfo?.label ?? objectName,
            iconUrl: objectInfo?.iconUrl,
            iconColor: objectInfo?.iconColor,
            queryFields: entityInfo?.queryFields?.length ? entityInfo.queryFields : entityInfo?.fields ?? ['Id'],
            fields: entityInfo?.fields ?? ['Id'],
            fieldMetadata: Object.values(metadata),
            display: this.mappingLookup[objectName]?.display,
          };
        });
      })
    );
  }

  getEntityMappings(): Observable<ISalesforceObjectMapping[]> {
    return this.mappingSubject.asObservable();
  }

  /**
   * Load Salesforce mappings from configuration
   */
  private initWhenIdentityReady(): void {
    const identity = this.identityStorage.get();
    if (this.hasValidIdentity(identity)) {
      console.log('[SalesforceDataSource] identity ready immediately');
      this.loadDynamicMappings();
      return;
    }

    console.log('[SalesforceDataSource] waiting for identity');
    this.identityStorage
      .observe()
      .pipe(
        filter((value) => this.hasValidIdentity(value)),
        take(1)
      )
      .subscribe(() => {
        console.log('[SalesforceDataSource] identity observed, loading mappings');
        this.loadDynamicMappings();
      });
  }

  private hasValidIdentity(identity: Identity | Readonly<Identity> | null | undefined): identity is Identity {
    return Boolean(identity?.token && identity?.namespace === Namespace.salesforce);
  }

  private loadDynamicMappings(): void {
    if (this.configLoadStarted) {
      console.log('[SalesforceDataSource] loadDynamicMappings already started');
      return;
    }
    this.configLoadStarted = true;
    console.log('[SalesforceDataSource] loading mappings from config');

    if (!this.mappings$) {
      this.mappings$ = this.loadMappingsFromConfig();
    }
    if (!this.objectInfos$) {
      this.objectInfos$ = this.mappings$.pipe(
        switchMap((mappings) => this.loadObjectInfos(mappings)),
        shareReplay(1),
      );
    }

    forkJoin({
      mappings: this.mappings$,
      objectInfos: this.objectInfos$,
    }).subscribe({
      next: ({ mappings, objectInfos }) => {
        const safeMappings = mappings ?? [];
        this.objectInfoLookup = objectInfos ?? {};
        console.log('[SalesforceDataSource] fetched mappings count', safeMappings.length);
        this.buildEntitySearchMapFromMappings(safeMappings);
        this.mappingSubject.next(safeMappings);
        console.log('[SalesforceDataSource] Loaded dynamic Salesforce mappings:', Object.keys(this.entitySearchMap));
        this.initialized$.next(true);
      },
      error: (error) => {
        console.error('Failed to load dynamic mappings', error);
        this.entitySearchMap = {};
        this.mappingLookup = {};
        this.objectInfoLookup = {};
        this.mappingSubject.next([]);
        this.initialized$.next(true);
      }
    });
  }

  private loadMappingsFromConfig(): Observable<ISalesforceObjectMapping[]> {
    const kpHost = this.getKpHost();

    if (!kpHost) {
      console.warn('[SalesforceDataSource] No kpHost available for loading Salesforce config');
      return of([]);
    }

    const url = `${kpHost}/adv2/config`;
    console.debug('[SalesforceDataSource] fetching config from', url);
    return this.http.get<IAdv2Config>(url).pipe(
      map((config) => {
        const mappings = config?.config?.salesforce?.objects ?? [];
        console.log('[SalesforceDataSource] raw salesforce config', config?.config?.salesforce);
        return mappings.filter(m => m.enabled !== false && Boolean(m.objectName));
      }),
      catchError((error) => {
        console.error('Failed to fetch Salesforce config', error);
        return of([]);
      }),
      shareReplay(1)
    );
  }

  private loadObjectInfos(mappings: ISalesforceObjectMapping[]): Observable<Record<string, ISalesforceObjectInfo>> {
    const kpHost = this.getKpHost();

    if (!kpHost) {
      console.warn('[SalesforceDataSource] No kpHost available for loading Salesforce object metadata');
      return of({});
    }

    const objectNames = Array.from(
      new Set(
        (mappings ?? [])
          .map((mapping) => mapping?.objectName?.trim())
          .filter((name): name is string => Boolean(name))
      )
    );
    if (!objectNames.length) {
      return of({});
    }

    const url = `${kpHost}/salesforce/meta/objects`;
    return this.http
      .get<ISalesforceObjectSummary[]>(url, { params: { objectNames: objectNames.join(','), resolveIcons: 'true' } })
      .pipe(
        map((objects) =>
          (objects ?? []).reduce((acc, object) => {
            const apiName = (object.apiName ?? object.name ?? '').trim();
            const label = object.label?.trim();
            if (apiName && label) {
              const objectInfo: ISalesforceObjectInfo = {
                label,
                iconUrl: object.iconUrl?.trim() || undefined,
                iconColor: object.iconColor?.trim() || undefined,
              };
              acc[apiName] = objectInfo;
              acc[apiName.toLowerCase()] = objectInfo;
            }
            return acc;
          }, {} as Record<string, ISalesforceObjectInfo>)
        ),
        catchError((error) => {
          console.error('Failed to fetch Salesforce object metadata', error);
          return of({});
        }),
        shareReplay(1)
      );
  }

  private resolveObjectInfo(objectName: string): ISalesforceObjectInfo | undefined {
    if (!objectName) {
      return undefined;
    }
    return this.objectInfoLookup[objectName] ?? this.objectInfoLookup[objectName.toLowerCase()];
  }

  private getKpHost(): string | undefined {
    const identity = this.identityStorage.get();
    const token = identity?.token;
    if (!token) {
      console.warn('No auth token available for loading Salesforce config');
      return this.environment?.kpHost;
    }

    try {
      const decoded: any = jwtDecode(token);
      return decoded?.kpHost ?? this.environment?.kpHost;
    } catch (error) {
      console.warn('[SalesforceDataSource] Unable to decode token for kpHost, falling back to environment', error);
      return this.environment?.kpHost;
    }
  }

  private buildEntitySearchMapFromMappings(mappings: ISalesforceObjectMapping[]): void {
    if (!mappings?.length) {
      console.warn('[SalesforceDataSource] buildEntitySearchMapFromMappings received no mappings');
      this.entitySearchMap = {};
      this.mappingLookup = {};
      return;
    }

    const newMap: IEntitySearchMap = {};
    const newLookup: Record<string, ISalesforceObjectMapping> = {};

    mappings.forEach((mapping) => {
      if (!mapping?.objectName) {
        console.warn('[SalesforceDataSource] mapping missing objectName', mapping);
        return;
      }

      const fields = this.extractFieldPaths(mapping);
      const queryFields = this.extractQueryFields(mapping);
      const metadata = this.buildFieldMetadata(mapping);

      newMap[mapping.objectName] = {
        fields,
        queryFields,
        includeIsDeleted: true,
        fieldMetadata: metadata,
      };
      newLookup[mapping.objectName] = mapping;
    });

    this.entitySearchMap = newMap;
    this.mappingLookup = newLookup;
  }

  private extractFieldPaths(mapping: ISalesforceObjectMapping): string[] {
    const paths = new Set<string>(['Id']);

    (mapping.fields ?? []).forEach((field) => {
      if (field?.path) {
        paths.add(field.path);
      }
    });

    return Array.from(paths);
  }

  private extractQueryFields(mapping: ISalesforceObjectMapping): string[] {
    const queryFields = new Set<string>();

    (mapping.fields ?? []).forEach((field) => {
      if (!field?.path) {
        return;
      }
      if (field.path.includes('__r.') && !field.path.endsWith('.Name')) {
        return;
      }
      queryFields.add(field.path);
    });

    if (!queryFields.size) {
      queryFields.add('Id');
    }

    return Array.from(queryFields);
  }

  private buildFieldMetadata(mapping: ISalesforceObjectMapping): Record<string, ISalesforceFieldMapping> {
    const metadata: Record<string, ISalesforceFieldMapping> = {};
    (mapping.fields ?? []).forEach((field) => {
      if (field?.path) {
        metadata[field.path] = field;
      }
    });
    return metadata;
  }

  get(entity: IEntity, getParams?: { fields: string }): Observable<any> {
    const fields = getParams?.fields ?? '';
    const url = this.buildApiUrl(`query?q=SELECT ${fields} FROM ${entity.type} WHERE Id='${entity.id}'`);
    return this.http.get<IResponse>(url).pipe(
      // force to match the IEntity interface
      map((response) => ({
        ...response.records[0],
        id: entity.id.toString(),
        type: entity.type,
      }))
    );
  }

// to get SF related entities (behind a feature flag)
  list(listParams: any): Observable<any[]> {
    const entityType = listParams?.entity?.type;
    const fields = Array.isArray(listParams?.fields) ? listParams.fields.join(', ') : listParams?.fields;
    if (!entityType || !fields) {
      return of([]);
    }

    let query = `SELECT ${fields} FROM ${entityType}`;
    if (listParams.query) {
      query += ` WHERE ${listParams.query}`;
    }
    if (listParams.orderBy) {
      query += ` ORDER BY ${listParams.orderBy}`;
    }
    query += ` LIMIT ${listParams.count ?? 100}`;

    const url = this.buildApiUrl('query?q=' + encodeURIComponent(query));
    return this.http.get<IResponse>(url).pipe(
      map((response) =>
        (response.records ?? []).map((record) => ({
          ...record,
          id: record.Id,
          type: entityType,
        }))
      )
    );
  }

  ping(): Observable<boolean> {
    return of(false);
  }

  query(entityType: string, queryParams: { searchTerm: string; count: number; queryFields: string[] }): Observable<IEntity[]> {
    const url = this.buildApiUrl('query');
    console.debug('query fields', queryParams);
    const query = this.constructSearchRequestQuery(entityType, queryParams);
    return this.http.get<IResponse>(url + query).pipe(
      map((response) =>
        response.records.map((entity) => ({
          ...entity,
          id: entity.Id,
          type: entityType,
        }))
      )
    );
  }

  private constructSearchRequestQuery(
    entityType: string,
    queryParams: { searchTerm: string; count: number; queryFields: string[] }
  ): string {
    let query = '';

    queryParams.searchTerm = queryParams.searchTerm.trim();
    const entityInfo = this.entitySearchMap[entityType];
    if (!entityInfo) {
      throw new Error(`Unknown entity type '${entityType}' building query.`);
    }

    const searchDetails: IEntitySearchDetails = {
      ...entityInfo,
      queryFields: queryParams.queryFields?.length ? queryParams.queryFields : entityInfo.queryFields,
    };

    query += this.generateSearchQuery(queryParams.searchTerm, searchDetails, entityType);

    return '?q=' + encodeURIComponent(query);
  }

  private generateSearchQuery(
    searchTerm: string,
    entityInfo: IEntitySearchDetails,
    entityType: string,
    count: number = 20,
    orderBy?: string
  ): string {
    const queryFields = entityInfo.queryFields ?? [];
    const metadataMap = entityInfo.fieldMetadata ?? {};
    const { includeIsDeleted } = entityInfo;
    const isDigit = /^\d+$/.test(searchTerm.trim());
    const escapedTerm = searchTerm.trim().replace(/'/g, "\\'");
    const fields = entityInfo.fields;
    let whereClause = '';
    // @TODO: determine the fields that could take digits
    if (isDigit) {
      whereClause = `Id = '${escapedTerm}'`;
    } else if (escapedTerm && queryFields.length) {
      const clauses = queryFields
        .filter((field) => !this.isIdField(field, metadataMap[field]))
        .map((field) => this.generateSearchClause(field, escapedTerm, metadataMap[field]))
        .filter((clause) => !!clause);
      whereClause = clauses.length ? `(${clauses.join(' OR ')})` : '';
    }

    if (includeIsDeleted) {
      whereClause += (whereClause ? ' AND ' : '') + 'IsDeleted = false';
    }

    // Final SOQL string
    let query = `SELECT ${fields.join(', ')} FROM ${entityType}`;
    if (whereClause) {
      query += ` WHERE ${whereClause}`;
    }
    if (orderBy) {
      query += ` ORDER BY ${orderBy}`;
    }
    query += ` LIMIT ${count}`;

    // @TODO: add offset for pagination later?
    return query;
  }

  private generateSearchClause(field: string, searchTerm: string, metadata?: ISalesforceFieldMapping): string {
    if (!searchTerm) {
      return '';
    }

    const escapedTerm = searchTerm.replace(/'/g, "\\'");
    const useLike = this.isTextFieldForSearch(field, metadata);

    if (!useLike) {
      return `${field} = '${escapedTerm}'`;
    }

    if (escapedTerm.includes(' ')) {
      const [first, last] = escapedTerm.split(' ', 2);
      return `(${field} LIKE '%${first}%' AND ${field} LIKE '%${last}%')`;
    }

    return `${field} LIKE '%${escapedTerm}%'`;
  }

  private isTextFieldForSearch(field: string, metadata?: ISalesforceFieldMapping): boolean {
    const normalized = metadata?.dataType?.toLowerCase();
    if (normalized) {
      if (normalized === 'text' || normalized === 'string') {
        return true;
      }
      if (normalized === 'id' || normalized === 'reference') {
        return false;
      }
    }

    const lowerPath = field.toLowerCase();
    if (lowerPath.endsWith('name')) {
      return true;
    }
    if (lowerPath.includes('__r.')) {
      return true;
    }
    if (lowerPath.endsWith('id')) {
      return false;
    }
    return true;
  }

  private isIdField(path: string, metadata?: ISalesforceFieldMapping): boolean {
    const normalized = metadata?.dataType?.toLowerCase();
    if (normalized === 'id' || normalized === 'reference') {
      return true;
    }
    const lowerPath = path.toLowerCase();
    return lowerPath === 'id' || lowerPath.endsWith('id');
  }

  // @TODO: filter fields client side as we have no option to specify fields to the sf api call
  meta(args: IMetaFunctionArgs): Observable<IEntityMetadata> {
    const url = this.buildApiUrl(`sobjects/${args.entity}/describe`);
    return this.http.get(url).pipe(
      map(
        (response: any) =>
          <IEntityMetadata>{
            name: response.name,
            label: response.label,
          }
      )
    );
  }

  buildApiUrl(path: string): string {
    const jwtPayload = jwtDecode<any>((this.identityStorage.get() as any).token);
    const base = `${jwtPayload.instanceUrl}`.replace(/\/+$/, '');
    if (!path) {
      return `${base}/services/data/${jwtPayload.apiVersion}`;
    }

    // If caller passes an absolute URL, just return it unchanged.
    if (/^https?:\/\//i.test(path)) {
      return path;
    }

    const normalizedPath = `${path}`.replace(/^\/+/, '');

    // If caller already included services/data in the path (e.g. nextRecordsUrl),
    // avoid double prefixing.
    if (normalizedPath.toLowerCase().startsWith('services/data/')) {
      return `${base}/${normalizedPath}`;
    }

    return `${base}/services/data/${jwtPayload.apiVersion}/${normalizedPath}`;
  }

  settings(settings: string): Observable<any> {
    return of(null);
  }

  listDocumentClient(listParams: any): Observable<any[]> {
    return of([]);
  }
}
